import { useAuth } from '@clerk/react';
import { useEffect, useRef } from 'react';
import i18n from '../../config/i18n';
import { useProfile } from '../../pages/profile/hooks/use-profile-queries';

/**
 * Applies `User.language` (saved server-side via the Settings language
 * picker) once per app session, so a user switching device lands back in
 * their own language instead of the browser default.
 *
 * Runs only on the *first* profile fetch after mount: later refetches (e.g.
 * the query invalidation right after the user picks a different language
 * from Settings) must not re-apply a value and fight the choice they just
 * made locally. A user with no saved language (`null`) is a no-op — the
 * existing browser/localStorage detection keeps deciding.
 */
export function useLanguageHydration(): void {
  const { isSignedIn } = useAuth();
  const { data: profile } = useProfile({ enabled: !!isSignedIn });
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current || !profile) {
      return;
    }
    hydratedRef.current = true;
    const savedLanguage = profile.language;
    if (savedLanguage && savedLanguage.split('-')[0] !== i18n.language.split('-')[0]) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [profile]);
}
